import TextField from 'material-ui/TextField'
import SelectField from 'material-ui/SelectField'
import MenuItem from 'material-ui/MenuItem'
import { Button, HelpEmail, AssetUploader, HelpLink } from 'components'
import SocialIcon from 'components/icons/SocialIcon'
import { default as SEVERITY_ICONS } from 'components/icons/SeverityIcons'

import { SocialLinks, ReviewApps } from 'utils/Enum'
import { debounce } from 'utils/Functions'
import UrlHelpers from 'helpers/UrlHelpers'

import './editStructuredData.scss'

const DEFAULT_VISIBLE_KEYS = ['fb', 'instagram', 'twitter']
const VISIBLE_KEY_COUNT = 3
const getVisibleKeys = valuesMap => {
  const fullKeys = Object.keys(valuesMap)
  if (fullKeys.length >= VISIBLE_KEY_COUNT) return fullKeys
  
  let defaultKeyIndex = 0
  let remainingGaps = VISIBLE_KEY_COUNT - fullKeys.length
  let keys = [...fullKeys]
  while (remainingGaps > 0) {
    const emptyKeyToAppend = DEFAULT_VISIBLE_KEYS[defaultKeyIndex]
    if (!fullKeys.includes(emptyKeyToAppend)) {
      keys.push(emptyKeyToAppend)
      remainingGaps--
    }

    defaultKeyIndex++
  }

  return keys
}

export default class EditStructuredData extends React.Component {
  static contextTypes = {
    currentUser: React.PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props)

    this.state = {
      values: {},
      visibleKeys: DEFAULT_VISIBLE_KEYS,
      reviewApp: 'autodetect',
      updatingLogo: false
    }

    this.setValue = this.setValue.bind(this)
    this.save = this.save.bind(this)
  }

  componentDidMount() {
    if (!this.props.data) return

    const { structuredDataSocialProfileLinks, structuredDataReviewApp } = this.props.data
    const stateUpdate = {}

    if (structuredDataSocialProfileLinks) {
      const linksMap = UrlHelpers.getSocialLinksMap(structuredDataSocialProfileLinks)

      stateUpdate.values = linksMap
      stateUpdate.visibleKeys = getVisibleKeys(linksMap)
    }

    if (structuredDataReviewApp) {
      stateUpdate.reviewApp = structuredDataReviewApp
    }
    
    this.setState(stateUpdate)
  }

  handleReviewAppChange(_e, _key, value) {
    const { data, onSave } = this.props

    onSave({
      ...data,
      structuredDataReviewApp: value,
    })
    this.setState({
      reviewApp: value,
    })
  }

  setValue(key, value) {
    const newState = Object.assign({}, this.state)
    newState.values[key] = value

    this.lazySave(newState)
    this.setState(newState)
  }

  showAllKeys() {
    this.setState({
      visibleKeys: [...Object.keys(SocialLinks), 'others'],
    })
  }

  save(state) {
    const { data, onSave } = this.props
    const { values } = state
    const keys = Object.keys(SocialLinks)
    const links = []

    keys.push('others')
    for (let key of keys) {
      if (!values[key]) continue
      links.push(values[key])
    }

    const oldLinksString = data.structuredDataSocialProfileLinks
    const newLinksString = links.join("\n")

    if (oldLinksString === newLinksString) return

    onSave({
      ...data,
      structuredDataSocialProfileLinks: newLinksString,
    })
  }

  saveLogoStatus(hasLogo) {
    const { data, onSave } = this.props

    this.setState({ updatingLogo: true }, () => {
      onSave({
        ...data,
        structuredDataDisplayLogo: hasLogo
      }, () => {
        this.setState({ updatingLogo: false })
      })
    })
  }

  lazySave = debounce((state) => {
    this.save(state)
  }, 2000)

  renderSocialProfile(key, isComposite) {
    if (!this.state.visibleKeys.includes(key)) return null

    const socialLink = SocialLinks[key]
    const placeholder = socialLink ? `ex. ${socialLink.prefix}/stockphototees` : 'Other links'

    return (
      <div className="edit-structured-data__social" key={key}>
        <div className="icon-container">
          {socialLink &&
            <SocialIcon
              brand={socialLink.icon}
              color={socialLink.color}
              fontSize="large"
            />
          }
        </div>
        <TextField
          placeholder={placeholder}
          fullWidth={true}
          value={this.state.values[key]}
          multiLine={isComposite}
          name={`structuredDataSocialProfileLink--${key}`}
          onChange={({ target: { value }}) => this.setValue(key, value)}
          onBlur={() => this.save(this.state)}
        />
      </div>
    )
  }

  renderSocialProfileStatus() {
    const { values } = this.state
    const socialLinks = Object.values(values)
    const socialLinksExist = socialLinks.filter(link => link).length > 0

    return SEVERITY_ICONS[socialLinksExist ? 'info' : 'optional']
  }

  renderReviewBlock() {
    return (
      <div className="edit-structured-data__review xlarge-spacing--top">
        <h3 className="small-spacing--bottom">
          {SEVERITY_ICONS[this.state.reviewApp ? 'info' : 'optional']}
          Which reviews would you like to display in Google Search?
        </h3>
        <SelectField
          name="reviewApp"
          onChange={::this.handleReviewAppChange}
          value={this.state.reviewApp}
        >
          {ReviewApps.map(app => (
            <MenuItem
              key={app.value}
              value={app.value}
              primaryText={app.label}
            />
          ))}
        </SelectField>
        <p>Using a different review app? Email us at <HelpEmail /> and let us know.</p>
      </div>
    )
  }

  render() {
    const socialKeys = Object.keys(SocialLinks)
    const allKeysVisible = this.state.visibleKeys.length === socialKeys.length + 1
    const shopThemeLink = `https://${this.context.currentUser.shop}/admin/themes`

    return (
      <div className="seosettings__row edit-structured-data">
        <div className="edit-structured-data__header small-spacing--bottom">
          <h2 className="edit-structured-data__header-title">Structured Data</h2>
          {this.props.saving && <span className="saving-indicator">Saving...</span>}
        </div>
        <ul className="icon-font-list">
          <HelpLink
            title="How do I set up my JSON-LD structured data?"
            url="https://support.pluginuseful.com/hc/en-us/articles/23534775850260-How-do-I-set-up-my-JSON-LD-structured-data-Schema-Markup"
          />
          <HelpLink
            title="How do search engines use structured data?"
            url="https://support.pluginuseful.com/hc/en-us/articles/23534813955220-How-do-search-engines-use-structured-data-What-are-the-best-practices"
          />
        </ul>

        <div className="edit-structured-data__help xxlarge-spacing--top">
          <h3 className="small-spacing--bottom">
            {SEVERITY_ICONS[this.props.data.structuredDataDisplayLogo ? 'info' : 'warning']}
            Add your company logo
          </h3>
          <AssetUploader
            filename="pluginseo_structuredDataLogo.png"
            label="Add Logo"
            url={this.props.data.structuredDataDisplayLogo ? this.props.data["structuredDataLogo.png"] : ''}
            processing={this.state.updatingLogo}
            afterUpload={() => {
              this.saveLogoStatus(true)
            }}
            afterDelete={() => {
              this.saveLogoStatus(false)
            }}
          />
        </div>
        {this.renderReviewBlock()}
        <div className="edit-structured-data__body xxlarge-spacing--top">
          <h3>
            {this.renderSocialProfileStatus()}
            What are your social profile links?
          </h3>
          {socialKeys.map(key => this.renderSocialProfile(key))}
          {this.renderSocialProfile('others', true)}
          {!allKeysVisible &&
            <div className="btn--add-more medium-spacing2--top">
              <Button
                label="Add more"
                buttonType="secondary"
                onTouchTap={::this.showAllKeys}
              />
            </div>
          }
        </div>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./app/components/plus/EditStructuredData.js